/******************************************************************************
 * @Author                : Gemini CLI                                        *
 * @CreatedDate           : 2026-06-01 12:00:00                               *
 *****************************************************************************/

import { Dialog, Button, Typography } from "@material-tailwind/react";
import { useState } from "react";
import toast from "react-hot-toast";
import { TrashIcon } from "@heroicons/react/24/outline";
import { t } from "i18next";
import { api } from "../../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../../services/api/error";
import Loading from "../../../../../../../components/loading/Loading";

export default function DeleteReportItemModal({
  open,
  report,
  onDelete,
  onClose
}: {
  open: boolean,
  report: EasyTech.ReportItem,
  onDelete: () => void,
  onClose: () => void
}) {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleDelete = () => {
    setIsLoading(true);
    api.delete(`admin/reports/${report.id}`)
      .then(() => {
        toast.success(t("settings:reports:deleteSuccess", "Report item deleted"));
        onDelete();
        onClose();
      })
      .catch((error) => {
        console.error(error);
        showApiErrorToast(error, { fallbackMessage: "Error deleting report item" });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Dialog open={open} handler={onClose} size="xs">
      <div className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-full bg-red-500">
            <TrashIcon className="h-5 w-5 text-white" />
          </div>
          <Typography variant="h5">
            {t("settings:reports:deleteTitle", "Delete report item")}
          </Typography>
        </div>
        
        <Typography className="mb-4">
          {t("settings:reports:deleteConfirm", "Are you sure you want to delete this item? This action cannot be undone.")}
          <br />
          <span className="font-semibold">{report.data}</span>
        </Typography>

        <div className="flex justify-end gap-2">
          <Button
            onClick={onClose}
            variant="outlined"
            color="blue-gray"
            size="sm"
            disabled={isLoading}
          >
            {t("common:cancel", "Cancel")}
          </Button>

          {isLoading ? (
            <Loading />
          ) : (
            <Button 
              color="red"
              onClick={handleDelete}
              size="sm"
            >
              {t("common:delete", "Delete")}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
